import React, { useMemo } from "react";
import { useChartFilters } from "./hooks/useChartFilters";
import { ChartTopFilters } from "../ChartTopFilters";
import { LineChart, LineChartProps } from "../LineChart";
import { aggregateData, RawDataPoint } from "./utils/aggregateData";
import {
  getPreviousYearRange,
  alignPreviousYearDates,
} from "./utils/dateUtils";

interface ChartCardProps
  extends Omit<LineChartProps, "data" | "interval" | "comparisonData"> {
  // Data from parent (different sources per instance)
  data: RawDataPoint[];
  comparisonData?: RawDataPoint[];

  // Chart configuration from parent
  title?: string;
  description?: string;
  yAxisLabel?: string;

  // Optional: default date range (defaults to current cycle)
  defaultDateRange?: {
    start: Date;
    end: Date;
  };

  menuOptions?: {
    label: string;
    onClick?: () => void;
  }[];
}

export const ChartCard: React.FC<ChartCardProps> = ({
  data,
  comparisonData,
  defaultDateRange,
  title,
  description,
  menuOptions,
  ...chartProps
}) => {
  // Internal filter state management (not exposed for reuse)
  const {
    state: filters,
    actions: filterActions,
    filtersCount,
  } = useChartFilters({
    initialState: defaultDateRange
      ? { dateRange: defaultDateRange }
      : undefined,
  });

  // Process data based on current filters (internal logic)
  const processedData = useMemo(() => {
    // Guard against undefined data
    if (!data || !Array.isArray(data)) {
      return {
        currentData: [],
        comparisonData: undefined,
      };
    }

    // Filter data by current date range
    const filteredData = data.filter(
      (point) =>
        point.date >= filters.dateRange.start &&
        point.date <= filters.dateRange.end,
    );

    // Process comparison data if enabled
    let filteredComparisonData: RawDataPoint[] = [];
    if (comparisonData && filters.compareTo === "previousYear") {
      const previousYearRange = getPreviousYearRange(filters.dateRange);
      const rawPreviousData = comparisonData.filter(
        (point) =>
          point.date >= previousYearRange.start &&
          point.date <= previousYearRange.end,
      );
      filteredComparisonData = alignPreviousYearDates(rawPreviousData);
    }

    // Aggregate data based on current interval
    return {
      currentData: aggregateData(filteredData, filters.interval),
      comparisonData:
        filteredComparisonData.length > 0
          ? aggregateData(filteredComparisonData, filters.interval)
          : undefined,
    };
  }, [data, comparisonData, filters]);

  return (
    <div
      className="flex flex-col gap-6 rounded-lg p-4"
      data-testid="chart-card"
    >
      <ChartTopFilters
        dateRange={filters.dateRange}
        selectedPreset={filters.selectedPreset}
        interval={filters.interval}
        compareTo={filters.compareTo}
        filtersCount={filtersCount}
        onPresetChange={filterActions.setPreset}
        onDateRangeChange={filterActions.setDateRange}
        onIntervalChange={filterActions.setInterval}
        onCompareToChange={filterActions.setCompareTo}
      />

      <LineChart
        data={processedData.currentData}
        interval={filters.interval}
        comparisonData={processedData.comparisonData}
        filtersCount={filtersCount}
        menuOptions={menuOptions}
        title={title}
        description={description}
        {...chartProps}
      />
    </div>
  );
};
